// Die gewachsenen Legacy-Widgets als React-Bausteine (Strangler Phase 5,
// Entschalen). Runbook: tools/REACT-STRANGLER.md.
//
// Aufteilung, die sich beim Entschalen als die richtige erwiesen hat:
//   DOM  -> React (hier)
//   VERHALTEN -> weiterhin die Legacy-Prototypen (CInput.gotKey/gotOK/
//                setFocus, CNumPad.clickWork/gotOK/gotCancel, CComponent.
//                setEnabled/reValue/reAttribute ...)
// Moeglich ist das, weil diese Methoden ausschliesslich ueber `this.id`,
// `this.properties` und document.getElementById arbeiten - sie brauchen
// keinen Widget-Baum, nur ein Handle mit dem richtigen Prototyp. Genau das
// liefert bridge.widget(..., { proto }). Der Baum aus CComponent-Objekten,
// den frueher der XML-Builder aufgebaut hat, entfaellt damit ersatzlos.
//
// Was hier NICHT passiert: nachbauen von Aussehen. Jeder Baustein bekommt
// seine Styles vom aufrufenden Screen, und der holt sie aus der
// Pixel-Baseline (tests/visual-baseline/*.html), nicht aus dem Gefuehl.
import { Fragment } from 'react';
import { CInput } from '../components/input.js';
import { CPushButton } from '../components/pushbutton.js';
import { CNumPad as CNumPadLegacy } from '../components/numpad.js';
import { CPagedPushPanel as CPagedPushPanelLegacy } from '../components/pagedpushpanel.js';
import { CInputField as CInputFieldLegacy } from '../components/inputfield.js';
import { CComponent } from '../components/component.js';
import { CClickNumber as CClickNumberLegacy } from '../components/clicknumber.js';
import { CMultiClickNumber as CMultiClickNumberLegacy } from '../components/multiclicknumber.js';
import { CMultiClickDate as CMultiClickDateLegacy } from '../components/multiclickdate.js';
import { CMultiClickTime as CMultiClickTimeLegacy } from '../components/multiclicktime.js';
import { CDataGrid as CDataGridLegacy } from '../components/datagrid.js';
import { CPre as CPreLegacy } from '../components/pre.js';
import { CTabbedPushPanel as CTabbedPushPanelLegacy } from '../components/tabbedpushpanel.js';
import { rasterStyle } from './secondaryPanel.jsx';

// --- Beschriftung ---------------------------------------------------------
// Eigenheit des Legacy-Builders, die bleiben MUSS: der Beschriftungs-WERT
// landet roh in der class ("CCaption POS-ID:"). Steht so in jeder Baseline.
// Reihenfolge der Klassen ist NICHT beliebig: der Builder schreibt erst die
// Zusatzklasse aus der XML, dann den Wert ("CCaption titletext Stornogrund").
export function Caption({ bridge, pfad, text, style, addclass }) {
  return (
    <p {...bridge.el(pfad, 'CCaption')} style={style}
       className={'CCaption' + (addclass ? ' ' + addclass : '') + ' ' + text}
       dangerouslySetInnerHTML={{ __html: text }} />
  );
}

// --- Tabellen-Container ---------------------------------------------------
// CContainerTable -> CContainerTableBody -> CContainerRow -> CContainerColumn.
// Pfade: Tabelle=pfad, Body=pfad:0, Zeile r=pfad:0:r, Spalte c=pfad:0:r:c.
// `zeilen` ist ein Array von Zeilen, jede Zeile ein Array von Spalten:
//   { style?, inhalt?: (spaltenPfad) => JSX }
export function ContainerTable({ bridge, pfad, style, zeilen }) {
  return (
    <table {...bridge.el(pfad, 'CContainerTable')} className="CContainerTable" style={style}>
      <tbody {...bridge.el(`${pfad}:0`, 'CContainerTableBody')} className="CContainerTableBody">
        {zeilen.map((spalten, r) => (
          <tr key={r} {...bridge.el(`${pfad}:0:${r}`, 'CContainerRow')} className="CContainerRow">
            {spalten.map((sp, c) => (
              <td key={c} {...bridge.el(`${pfad}:0:${r}:${c}`, 'CContainerColumn')}
                  className="CContainerColumn" style={sp.style}>
                {sp.inhalt ? sp.inhalt(`${pfad}:0:${r}:${c}`) : null}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --- Textzeile ------------------------------------------------------------
// CTextLabel ist im Legacy nichts weiter als eine Tabelle mit EINER Zelle,
// in der eine Beschriftung sitzt.
export function TextLabel({ bridge, pfad, style, text, textStyle, spaltenStyle, addclass,
                           ausHandle = false }) {
  // ausHandle: Text und Sichtbarkeit kommen aus dem Handle statt aus Props.
  // Gebraucht fuer die beiden Summen-Label des Hauptbildschirms, auf die der
  // Bonrenderer schreibt (properties.styles.visibility + reStyle(),
  // caption.properties.value + caption.reDraw()). Ohne das macht das naechste
  // React-Rendern jeden dieser Schreibvorgaenge zunichte - und gerendert wird
  // bei jeder Bon-Aenderung.
  const label = ausHandle ? bridge.handleAt(pfad) : null;
  const eigenerStil = (label && label.properties && label.properties.styles) || null;
  const stil = eigenerStil ? { ...style, ...eigenerStil } : style;
  const inhaltsCaption = (sp) => (ausHandle
    ? <SchreibCaption bridge={bridge} pfad={`${sp}:0`} style={textStyle} />
    : <Caption bridge={bridge} pfad={`${sp}:0`} text={text} style={textStyle} />);
  return (
    <div {...bridge.el(pfad, 'CTextLabel')} style={stil}
         className={'CTextLabel' + (addclass ? ' ' + addclass : '')}>
      <ContainerTable bridge={bridge} pfad={`${pfad}:0`} zeilen={[[{
        style: spaltenStyle,
        inhalt: inhaltsCaption,
      }]]} />
    </div>
  );
}

// --- Rahmen (CFrame) -------------------------------------------------------
// Reine Huelle: ein <div>, das im Legacy NUR "position: absolute" mitbringt
// (components/frame.js) und alles weitere aus der Screen-XML bekommt. In
// CMainPOS traegt es zweimal: den Bonbehaelter (bonContainer, darin
// CDataGrid + CPre) und die Statuszeile (statusBar, darin drei CCaption).
// Beide werden von fremdem Code ueber ihren NAMEN gegriffen
// (bonContainer.components.bon, statusBar.components.dateTimeInfo) - wer
// einen Frame benutzt, muss seine Kinder also benennen, sonst faellt der
// Zugriff spaeter und woanders auf die Nase.
export function Frame({ bridge, pfad, style, addclass, children }) {
  return (
    <div {...bridge.el(pfad, 'CFrame')} style={{ position: 'absolute', ...style }}
         className={'CFrame' + (addclass ? ' ' + addclass : '')}>
      {children}
    </div>
  );
}

// --- Bild (CImage) ---------------------------------------------------------
// Ein <img> mit "position: absolute" (components/image.js). Zwei Eigenheiten
// des Legacy-Builders sind hier nachgebildet:
//  - Das src kommt aus properties.attributes, nicht aus einem eigenen Prop -
//    fremder Code schreibt direkt dorthin und ruft reAttribute()
//    (CScreenSaver.updateImage tut genau das ueber das cameraImage).
//  - React laesst ein LEERES src weg, der Legacy-Builder schreibt src=" ".
//    Deshalb wird ein fehlendes src bewusst als " " gesetzt statt weggelassen:
//    ohne das driftet die Baseline (Galerie-Eintrag 33 haelt genau src=" "
//    fest).
export function Image({ bridge, pfad, style, addclass, attributes = {} }) {
  const attr = { src: ' ', ...attributes };
  return (
    <img {...bridge.el(pfad, 'CImage')} {...attr}
         style={{ position: 'absolute', ...style }}
         className={'CImage' + (addclass ? ' ' + addclass : '')} />
  );
}

// Verdrahtung eines CTextLabel, auf das Legacy-Code schreibt.
// CTextLabel.prepare legt im Legacy ein CCaption-KIND an und haengt es als
// this.caption ans Label. Der Bonrenderer arbeitet genau darueber:
//   this.SumCaption.caption.properties.value = text2;   (bonrenderer.js:963)
//   this.SumCaption.caption.reDraw();                   (bonrenderer.js:969)
// Fehlt das .caption, wirft switchSumInfo "Cannot read properties of
// undefined (reading 'properties')" - und weil das INNERHALB eines
// jQuery-.done() passiert, wird die Bonier-Kette nie aufgeloest: die Kasse
// haengt, ohne einen Fehler zu zeigen.
// Der Pfad des Captions folgt dem Legacy-Baum:
//   CTextLabel -> Tabelle(:0) -> Body(:0:0) -> Zeile(:0:0:0)
//              -> Spalte(:0:0:0:0) -> Caption(:0:0:0:0:0)
export function verdrahteTextLabel(bridge, pfad) {
  const label = bridge.handleAt(pfad);
  const caption = captionVorbereiten(bridge, `${pfad}:0:0:0:0:0`);
  caption.reDraw = function () { this.prepare(); };
  label.caption = caption;
  return label;
}

// --- Eingabefeld ----------------------------------------------------------
// Das eigentliche <input>. Der Prototyp ist CInput, damit gotKey/gotOK/
// gotBackspace/gotClear/onFocus/onBlur/onMousedown UNVERAENDERT die alten
// sind - daran haengen NumPad, Scanner und die OK-Rueckrufe der Screens.
// events (24.8.2026): _handle setzt handle.properties.events bei JEDEM
// Rendern auf das, was die JSX erklaert - ein in mountLegacy nachtraeglich
// gesetzter Handler wird also beim naechsten Rendern weggewischt. Wer einen
// ECHTEN Handler braucht (CMainPOS haengt restoreInputFocus an onBlur), muss
// ihn deshalb hier durchreichen, nicht spaeter anhaengen.
export function Input({ bridge, pfad, style, properties = {}, attributes = {}, events = {} }) {
  // Voreinstellungen des Legacy-CInput-Konstruktors, woertlich.
  const grund = {
    type: 'all',
    maxLength: 99,
    sound: true,
    OKcallback: { cls: null, fnc: null },
    changeValueCallback: { cls: null, fnc: null },
    selectedStyles: {},
    // properties.styles muss DIESELBEN Werte tragen wie das React-style:
    // CInput.onFocus/onBlur schreiben sie ueber reStyle() zurueck ins DOM.
    styles: { ...style },
  };
  const props = bridge.widget(pfad, 'CInput', {
    events: { onKeyup: true, onBlur: true, onFocus: true, onMousedown: true, ...events },
    attributes: { value: '', maxlength: '50', readonly: 'readonly', ...attributes },
    properties: { ...grund, ...properties },
    proto: CInput.prototype,
  });
  // React verwaltet den Wert NICHT - das tut CInput ueber reAttribute().
  // defaultValue statt value, sonst macht React ein kontrolliertes Feld
  // daraus und warnt bei jedem Tastendruck.
  // `typ` wird zusaetzlich als React-Attribut gesetzt (das PIN-Feld ist ein
  // type="password"), damit React es nicht bei einem Re-Render wegnimmt.
  return <input {...props} className="CInput" style={style} defaultValue="" readOnly
                type={attributes.type} />;
}

// CInputField: Tabelle mit dem Eingabefeld und optional Ruecktaste/Loeschen.
// Die beiden Knoepfe rufen CInputField.gotBackspace/gotClear - die holen
// sich das Eingabefeld ueber POS.getComponentById(onClick_parameter_0).
// addclass (24.8.2026, fuer CMainPOS): das Eingabefeld des Hauptbildschirms
// traegt zusaetzlich .CMainPOSInput, und dort steckt sein Layout. Dieselbe
// lautlose Falle wie beim PushPanel - ein unbekanntes Prop verschluckt JSX
// kommentarlos.
export function InputField({ bridge, pfad, style, spaltenStyle, inputStyle, knopfStyle,
                            backspace = true, clear = true, inputProperties, inputAttributes,
                            inputEvents, addclass }) {
  // Pfade wie im Legacy-Baum: CInputField -> Tabelle(:0) -> Body(:0:0) ->
  // Zeile(:0:0:0) -> Spalte(:0:0:0:c) -> Inhalt(:0). Die Zellen-Pfade
  // liefert ContainerTable an `inhalt`, der Inhalt haengt eine Ebene tiefer.
  const inputId = bridge.childId(`${pfad}:0:0:0:0:0`);

  // Dieselbe Legacy-Regel eine Ebene tiefer (CInputField.prepare): die
  // Spalte des Eingabefeldes uebernimmt dessen Breite.
  const inputBreite = inputStyle && inputStyle.width;
  const inputSpalte = spaltenStyle || (inputBreite ? { width: inputBreite } : undefined);

  const spalten = [{
    style: inputSpalte,
    inhalt: (sp) => <Input bridge={bridge} pfad={`${sp}:0`} style={inputStyle}
                           properties={inputProperties} attributes={inputAttributes}
                           events={inputEvents} />,
  }];

  // Beschriftung der Ruecktaste ist im Legacy nie gesetzt - der Builder
  // schreibt darum die Zeichenkette "undefined" in die class. Sieht falsch
  // aus, steht aber so in jeder Baseline und bleibt deshalb.
  if (backspace) spalten.push({
    inhalt: (sp) => <FieldButton bridge={bridge} pfad={`${sp}:0`} klasse="backbutton"
                                 text="undefined" leer style={knopfStyle} inputId={inputId} />,
  });
  if (clear) spalten.push({
    inhalt: (sp) => <FieldButton bridge={bridge} pfad={`${sp}:0`} klasse="clearbutton"
                                 text="C" style={knopfStyle} inputId={inputId} />,
  });

  return (
    <div {...bridge.el(pfad, 'CInputField', { proto: CInputFieldLegacy.prototype })}
         className={'CInputField' + (addclass ? ' ' + addclass : '')} style={style}>
      <ContainerTable bridge={bridge} pfad={`${pfad}:0`} zeilen={[spalten]} />
    </div>
  );
}

// Ruecktaste/Loeschen neben einem Eingabefeld.
function FieldButton({ bridge, pfad, klasse, text, leer, style, inputId }) {
  const handler = klasse === 'backbutton'
    ? (component, parameters) => { const c = POS.getComponentById(parameters[0]); c.setFocus(); c.gotBackspace(); }
    : (component, parameters) => { const c = POS.getComponentById(parameters[0]); c.setFocus(); c.gotClear(); };
  return (
    <div {...bridge.widget(pfad, 'CPushButton', {
           events: { onClick: handler, onMouseout: true, onMouseup: true },
           attributes: { onClick_parameter_0: inputId },
           proto: CPushButton.prototype,
         })}
         className={'CPushButton ' + klasse} style={style}>
      <p {...bridge.el(`${pfad}:0`, 'CCaption')} className={'CCaption ' + text}
         dangerouslySetInnerHTML={{ __html: leer ? '' : text }} />
    </div>
  );
}

// CLabeledInputField: Beschriftung links, Eingabefeld rechts.
// Die Beschriftung bringt CLabeledInputField im Legacy schon von sich aus
// mit: margin auto, marginRight 0.1em, fontSize 2.8vh. Wer nichts angibt,
// bekommt genau das (aufgefallen am PIN-Feld der Kassa-Uebergabe, dem sonst
// die Schriftgroesse fehlte).
const BESCHRIFTUNG_VORGABE = { margin: 'auto', marginRight: '0.1em', fontSize: '2.8vh' };

// `handleProperties` sind Legacy-Properties, die auf dem HANDLE des
// beschrifteten Feldes liegen muessen - nicht auf dem Eingabefeld darin.
// CPosBalancing.sumValues liest z.B. den Umrechnungsfaktor als
// `inputs[i].properties.field.input.attributes.fact`.
export function LabeledInputField({ bridge, pfad, style, label,
                                    labelStyle = BESCHRIFTUNG_VORGABE, labelSpalteStyle,
                                    feldSpalteStyle, feldProps, addclass, handleProperties }) {
  // Legacy-Regel aus CLabeledInputField.prepare: hat das FELD eine Breite,
  // bekommt seine Tabellenspalte dieselbe. Dasselbe gilt fuer die
  // Beschriftung. Wer die Spaltenbreite ausdruecklich angibt, gewinnt.
  const feldBreite = feldProps && feldProps.style && feldProps.style.width;
  const feldSpalte = feldSpalteStyle || (feldBreite ? { width: feldBreite } : undefined);
  const labelBreite = labelStyle && labelStyle.width;
  const labelSpalte = labelSpalteStyle || (labelBreite ? { width: labelBreite } : undefined);

  return (
    <div {...bridge.el(pfad, 'CLabeledInputField', { properties: handleProperties })} style={style}
         className={'CLabeledInputField' + (addclass ? ' ' + addclass : '')}>
      <ContainerTable bridge={bridge} pfad={`${pfad}:0`} zeilen={[[
        {
          style: labelSpalte,
          inhalt: (sp) => <Caption bridge={bridge} pfad={`${sp}:0`} text={label} style={labelStyle} />,
        },
        {
          style: feldSpalte,
          inhalt: (sp) => <InputField bridge={bridge} pfad={`${sp}:0`} {...feldProps} />,
        },
      ]]} />
    </div>
  );
}

// --- Zehnerblock ----------------------------------------------------------
// Tastenbelegung WOERTLICH aus CNumPad.prototype.prepare. Nachgebaut ist nur
// die Auswahl der Tasten; geklickt wird weiterhin in die Legacy-Methoden.
const NUMPAD_TASTEN = {
  phoneAmount: { keys: [1, 2, 3, 4, 5, 6, 7, 8, 9, ',', 0, '00'], spalten: 3 },
  keyboardAmount: { keys: [7, 8, 9, 4, 5, 6, 1, 2, 3, 0, '00', ','], spalten: 3 },
  phone: { keys: [1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, null], spalten: 3, ok: 11, next: 11, cancel: 9 },
  keyboard: { keys: [7, 8, 9, 4, 5, 6, 1, 2, 3, 0, null, null], spalten: 3, ok: 10, next: 10, cancel: 11 },
  message: { keys: [1, 2, 3, 4, 5, 6, 7, 8, 9, 'BS', 0, 'RET'], spalten: 3 },
};
// FRUEHER stand hier eine NUMPAD_WERTE-Tabelle mit Punkten
// ([..., '.', 0, '.00']), nachgebaut aus dem keyValues-Array in
// components/numpad.js:41. Das war eine Fehlannahme: das Legacy DEKLARIERT
// dieses Array zwar, benutzt es fuer gewoehnliche Tasten aber NIE - Zeile 81
// setzt `keyValue = keys[i].toString()`, also die BESCHRIFTUNG. keyValues
// greift nur bei den Sondertasten BS/RET (Zeilen 106/117), und dort sind
// beide Listen ohnehin gleich.
// Aufgefallen erst bei CMainPOS: es ist der erste Bildschirm mit dem Typ
// "phoneAmount", wo sich keys und keyValues unterscheiden. Die Folge war
// bitter unauffaellig - CInput.checkMoneyValue rechnet ausschliesslich mit
// KOMMA (es zerlegt an "," und prueft key == ","), ein Punkt faellt bei ihm
// einfach durch: das Universalfeld zeigte "5.00" statt "5,00", und jede
// Eingabe eines Betrags war damit still falsch.

export function NumPad({ bridge, pfad, typ, ok = false, cancel = false, next = false,
                         style, panelStyle, knopfStyle }) {
  const belegung = NUMPAD_TASTEN[typ];
  const keys = belegung.keys.slice();
  if (ok) keys[belegung.ok] = 'OK';
  if (next) keys[belegung.next] = 'NX';
  if (cancel) keys[belegung.cancel] = 'XX';
  const werte = keys; // keyValue = Beschriftung, siehe Kommentar oben
  const zeilen = Math.ceil(keys.length / belegung.spalten);

  return (
    <span {...bridge.el(pfad, 'CNumPad', { proto: CNumPadLegacy.prototype })}
          className="CNumPad" style={style}>
      <div {...bridge.el(`${pfad}:0`, 'CPushPanel')} className="CPushPanel" style={panelStyle}>
        {keys.map((k, i) => (
          <NumPadKey key={i} bridge={bridge} pfad={`${pfad}:0:${i}`} numpadId={bridge.childId(pfad)}
                     taste={k} wert={werte[i]} style={knopfStyle}
                     raster={rasterStyle(belegung.spalten, zeilen, i)} />
        ))}
      </div>
    </span>
  );
}

function NumPadKey({ bridge, pfad, numpadId, taste, wert, style, raster }) {
  // Leere Rasterzelle: der Legacy-CPushPanel legt trotzdem einen Knopf an
  // und macht ihn unsichtbar.
  if (taste == null) {
    // Auch die leere Taste bekommt ihre (leere) Beschriftung - der
    // Legacy-Builder legt sie an, und im DOM steht deshalb ein
    // <p class="CCaption undefined"></p>. Faellt nur auf, wenn ein NumPad
    // ueberhaupt leere Tasten hat (Typ "phone" ohne Abbrechen, z.B. in
    // CPosBalancing).
    return (
      <div {...bridge.widget(pfad, 'CPushButton', { proto: CPushButton.prototype })}
           className="CPushButton" style={{ visibility: 'hidden', ...style, ...raster }}>
        <p {...bridge.el(`${pfad}:0`, 'CCaption')} className="CCaption undefined" />
      </div>
    );
  }

  const sonder = { OK: 'OK', XX: 'X', NX: null }[taste];
  const beschriftung = taste === 'OK' ? '' : (taste === 'XX' ? 'X' : (taste === 'NX' ? '' : String(taste)));
  const eigen = { // Handler und Zusatz-Attribute der Sondertasten
    OK: { fnc: CNumPadLegacy.prototype.gotOK, param0: numpadId },
    NX: { fnc: CNumPadLegacy.prototype.gotOK, param0: numpadId },
    XX: { fnc: CNumPadLegacy.prototype.gotCancel, param0: numpadId },
  }[taste] || { fnc: CNumPadLegacy.prototype.clickWork, param0: null };

  const nxStyle = taste === 'NX' ? {
    backgroundImage: 'url(lib/img/bonedit/07scrolldown.png)',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
  } : null;

  return (
    <div {...bridge.widget(pfad, 'CPushButton', {
           events: { onMousedown: eigen.fnc, onMouseout: true, onMouseup: true },
           attributes: eigen.param0 ? { onMousedown_parameter_0: eigen.param0 } : {},
           properties: { keyValue: String(wert) },
           proto: CPushButton.prototype,
         })}
         className={'CPushButton' + (sonder ? ' ' + sonder : '')}
         style={{ ...style, ...nxStyle, ...raster }}>
      <p {...bridge.el(`${pfad}:0`, 'CCaption')} className={'CCaption ' + beschriftung}
         dangerouslySetInnerHTML={{ __html: beschriftung }} />
    </div>
  );
}

// --- Nach dem Mount: die Objektbeziehungen des Legacy-Baums -----------------
// Der XML-Builder hat nicht nur DOM erzeugt, sondern auch Verweise gesetzt,
// auf die Screen-Methoden bauen: labeled.field.input.setFocus(),
// numPad.controlFields.push(input). React erzeugt die Handles erst beim
// Rendern - diese Verdrahtung gehoert deshalb in spec.mountLegacy, das nach
// dem Mount laeuft. Zurueck kommt das CInput-Handle, an dem die Screens
// ihre OKcallbacks aufhaengen.
// Ein CInputField ohne Beschriftung (CPosReceiptList): fremder Code greift
// ueber `components.inputField.input` darauf zu (CEANManager schreibt dort
// die Kundenkarte hinein, CMainPOS setzt nach dem Zeichnen den Fokus).
export function verdrahteInputField(bridge, pfad) {
  const feld = bridge.handleAt(pfad);
  const input = bridge.handleAt(`${pfad}:0:0:0:0:0`);
  feld.input = input;
  // backspace/clear (24.8.2026): CInputField hat ein EIGENES setEnabled, das
  // NICHT sich selbst deaktiviert, sondern nur diese beiden Knoepfe
  // (inputfield.js:124-127) - dieselbe Bauart wie bei CPagedPushPanel. Ohne
  // die Verweise (und ohne den Prototyp am Handle) greift die Vorgabe aus
  // CComponent, und die Klasse "disabled" landet am FELD statt an den
  // Knoepfen. Aufgefallen an der Warengruppen-Ueberlagerung, die
  // components.info.setEnabled(false) ruft.
  const backspace = bridge.handleAt(`${pfad}:0:0:0:1:0`);
  const clear = bridge.handleAt(`${pfad}:0:0:0:2:0`);
  if (backspace) feld.backspace = backspace;
  if (clear) feld.clear = clear;
  return input;
}

export function verdrahteLabeledInputField(bridge, pfad) {
  const feldPfad = `${pfad}:0:0:0:1:0`;
  const labeled = bridge.handleAt(pfad);
  const feld = bridge.handleAt(feldPfad);
  const input = bridge.handleAt(`${feldPfad}:0:0:0:0:0`);
  labeled.field = feld;
  feld.input = input;
  return input;
}

// --- Geblaettertes Knopf-Panel --------------------------------------------
// CPagedPushPanel, Fuellart "array" (die einzige, die die portierten Screens
// benutzen). Die Seitenrechnung ist WOERTLICH die aus
// CPagedPushPanel.prototype.arrayFill:
//   Seiten = ceil((n-2)/(platz-2))    (nur wenn platz > 2 und platz < n)
//   je Seite: platz + c echte Knoepfe, c = -1 wenn nicht letzte Seite,
//   nochmal -1 wenn nicht erste - der Rest sind Blaetterknoepfe.
// Leere Rasterzellen bekommen - wie im Legacy - einen unsichtbaren Knopf
// OHNE Breite/Hoehe (CPushPanel.prepare setzt beides nur fuer belegte
// Zellen). Das steht so in tests/visual-baseline/14-overlay-bonstorno.html.
export function seitenAufteilen(knoepfe, zeilen, spalten) {
  const platz = zeilen * spalten;
  let seitenZahl = 1;
  if (platz > 2 && platz < knoepfe.length)
    seitenZahl = Math.ceil((knoepfe.length - 2) / (platz - 2));

  const seiten = [];
  let b = 0;
  for (let p = 0; p < seitenZahl; p++) {
    let c = p < seitenZahl - 1 ? -1 : 0;
    if (p > 0) c -= 1;

    const zelle = [];
    if (p > 0) zelle.push({ art: 'prev' });
    for (let i = 0; i < platz + c; i++) {
      if (knoepfe[b] !== undefined) zelle.push({ art: 'knopf', knopf: knoepfe[b] });
      b++;
    }
    if (p < seitenZahl - 1) zelle.push({ art: 'next' });
    while (zelle.length < platz) zelle.push({ art: 'leer' });
    seiten.push(zelle);
  }
  return seiten;
}

// Die ZWEITE Fuellart von CPagedPushPanel ("xy", CPagedPushPanel.xyFill): die
// Knoepfe bringen ihren Platz selbst mit - Seite, Spalte und Zeile stehen in
// der BTN-Tabelle. Daran haengen die Produkttasten des Hauptbildschirms.
// Unterschiede zu "array", die beide belegt sind (Live-Vorlage des
// Hauptbildschirms): die Blaetterknoepfe UEBERSCHREIBEN den Platz, auf dem
// sie sitzen (erste bzw. letzte Zelle), und die erste Seite bekommt
// visibility "" statt "visible" - ein leerer String, den der Browser gar
// nicht ins style-Attribut schreibt.
export function xySeitenAufteilen(knoepfe, zeilen, spalten) {
  const platz = zeilen * spalten;
  let seitenZahl = 0;
  const belegt = [];

  for (const k of knoepfe) {
    const seite = Number(k.seite) || 1;
    const absPos = (Number(k.zeile) - 1) * spalten + (Number(k.spalte) - 1);
    if (seite > seitenZahl) seitenZahl = seite;
    if (!belegt[seite - 1]) belegt[seite - 1] = [];
    belegt[seite - 1][absPos] = k;
  }

  const seiten = [];
  for (let p = 0; p < seitenZahl; p++) {
    const zellen = [];
    for (let n = 0; n < platz; n++) {
      if (p > 0 && n === 0) { zellen.push({ art: 'prev' }); continue; }
      if (seitenZahl > p + 1 && n === platz - 1) { zellen.push({ art: 'next' }); continue; }
      const k = (belegt[p] || [])[n];
      zellen.push(k ? { art: 'knopf', knopf: k } : { art: 'leer' });
    }
    seiten.push(zellen);
  }
  return seiten;
}

// proto (24.8.2026): CPagedPushPanel hat ein EIGENES setEnabled, das nicht
// sich selbst deaktiviert, sondern seine aktuelle Seite
// (pagedpushpanel.js:265-268). Ohne den Prototyp greift die Vorgabe aus
// CComponent, und die Klasse "disabled" landet am Panel statt an der Seite
// darin - in der Baseline steht sie eine Ebene tiefer.
// Erst bei CMainPOS aufgefallen: es ist der erste Screen, dessen Overlays das
// gepagete Panel wirklich deaktivieren.
export function PagedPushPanel({ bridge, pfad, zeilen = 1, spalten = 1, knoepfe,
                                 style, panelStyle, knopfStyle, art = 'array',
                                 prevText = '&lt;', nextText = '&gt;' }) {
  const seiten = art === 'xy'
    ? xySeitenAufteilen(knoepfe, zeilen, spalten)
    : seitenAufteilen(knoepfe, zeilen, spalten);
  const pagedId = bridge.childId(pfad);

  return (
    <span {...bridge.el(pfad, 'CPagedPushPanel', {
            properties: { styles: { ...style } },
            proto: CPagedPushPanelLegacy.prototype,
          })}
          className="CPagedPushPanel" style={style}>
      {seiten.map((zellen, p) => {
        // "xy" setzt fuer die erste Seite einen LEEREN String - der landet
        // nicht im style-Attribut. "array" schreibt "visible".
        const sichtbar = p === 0 ? (art === 'xy' ? '' : 'visible') : 'hidden';
        const panelPfad = `${pfad}:${p}`;
        const panelId = bridge.childId(panelPfad);
        // Jede Seite ERBT die Styles des Paged-Panels: im Legacy macht das
        // combine(panels[p].properties, this.properties) in arrayFill. Ohne
        // das steht eine Seite ohne Groesse und Position da (bei
        // CTimeRecord sofort als 33% Bildabweichung aufgefallen).
        const stil = { ...style, ...panelStyle, visibility: sichtbar };
        return (
          <div key={p} {...bridge.el(panelPfad, 'CPushPanel', { properties: { styles: { ...stil } } })}
               className="CPushPanel" style={stil}>
            {zellen.map((z, i) => (
              <SeitenKnopf key={i} bridge={bridge} pfad={`${panelPfad}:${i}`} zelle={z}
                           pagedId={pagedId} panelId={panelId} knopfStyle={knopfStyle}
                           raster={rasterStyle(spalten, zeilen, i, {
                             ...knopfStyle, ...(z.art === 'knopf' ? z.knopf.style : null),
                           })}
                           prevText={prevText} nextText={nextText} />
            ))}
          </div>
        );
      })}
    </span>
  );
}

function SeitenKnopf({ bridge, pfad, zelle, pagedId, panelId, knopfStyle, raster, prevText, nextText }) {
  // Leere Zelle: unsichtbar und OHNE gerechnete Hoehe - CPushPanel setzt die
  // nur fuer BELEGTE Zellen. Was der defaultButton der Layout-XML mitgibt
  // (z.B. eine feste Breite), bekommt sie trotzdem: das laeuft im Legacy
  // ueber combine(..., properties.defaultButton) und steht so in der
  // Live-Vorlage von CTimeRecord.
  if (zelle.art === 'leer') {
    return (
      <div {...bridge.widget(pfad, 'CPushButton', { proto: CPushButton.prototype })}
           className="CPushButton"
           style={{ position: 'absolute', ...knopfStyle, visibility: 'hidden',
                    top: raster.top, left: raster.left }}>
        <p {...bridge.el(`${pfad}:0`, 'CCaption')} className="CCaption undefined" />
      </div>
    );
  }

  const blaettern = zelle.art === 'prev' || zelle.art === 'next';
  const text = zelle.art === 'prev' ? prevText : (zelle.art === 'next' ? nextText : zelle.knopf.text);
  const fnc = zelle.art === 'prev' ? CPagedPushPanelLegacy.prototype.prevPage
    : (zelle.art === 'next' ? CPagedPushPanelLegacy.prototype.nextPage : zelle.knopf.handler);

  const attributes = blaettern
    ? { onMousedown_parameter_0: pagedId, onMousedown_parameter_1: panelId }
    : {};
  if (!blaettern) {
    const k = zelle.knopf;
    if (k.param0 !== undefined) attributes.onMousedown_parameter_0 = k.param0;
    if (k.param1 !== undefined) attributes.onMousedown_parameter_1 = k.param1;
    if (k.param2 !== undefined) attributes.onMousedown_parameter_2 = k.param2;
  }

  return (
    <div {...bridge.widget(pfad, 'CPushButton', {
           events: { onMousedown: fnc, onMouseout: true, onMouseup: true },
           attributes,
           proto: CPushButton.prototype,
         })}
         className={'CPushButton' + (!blaettern && zelle.knopf.klasse ? ' ' + zelle.knopf.klasse : '')}
         style={{ ...knopfStyle, ...(blaettern ? {} : zelle.knopf.style), ...raster }}>
      <p {...bridge.el(`${pfad}:0`, 'CCaption')} className={'CCaption ' + text}
         dangerouslySetInnerHTML={{ __html: text }} />
    </div>
  );
}

// Nach dem Mount: die Seiten verketten. nextPage/prevPage hangeln sich ueber
// panel.nextPanel/prevPanel weiter und merken die aktuelle Seite am
// CPagedPushPanel - im Legacy stellte arrayFill diese Verweise her.
export function verdrahtePagedPushPanel(bridge, pfad) {
  const paged = bridge.handleAt(pfad);
  const seiten = paged.children.filter(Boolean);
  for (let p = 0; p < seiten.length; p++) {
    if (p > 0) {
      seiten[p].prevPanel = seiten[p - 1];
      seiten[p - 1].nextPanel = seiten[p];
    }
  }
  paged.currentPanel = seiten[0] || null;
  paged.panels = seiten;
  paged.pages = seiten.length;
  return paged;
}

// --- Einfaches Knopf-Panel -------------------------------------------------
// CPushPanel ohne Blaettern. `knoepfe` sind Daten: { text, handler, param0..2,
// style, klasse }. Breite/Hoehe eines Knopfes gewinnen ueber die
// Rasterrechnung (s. rasterStyle).
// addclass (24.8.2026, fuer CMainPOS): leftPad und infoIcons holen ihr
// LAYOUT aus CSS-Klassen (.leftpushpanel, .infoicons in lib/css/pos.css),
// nicht aus inline-Styles. Fehlt die Klasse, ist der Screen kaputt - und
// zwar lautlos, weil ein unbekanntes Prop in JSX einfach verschluckt wird.
// Die REIHENFOLGE der Stil-Eigenschaften eines Panel-Knopfs, wie
// CPushPanel.prepare sie erzeugt (components/pushpanel.js:52-70):
//   1. die eigenen Stile des Knopfs
//   2. width und height - angehaengt, BEVOR die Panel-Vorgabe gemischt wird
//      (Zeilen 52-56); die Vorgabe ueberschreibt danach nur noch die WERTE,
//      nicht die Position im Schluesselband
//   3. position: absolute
//   4. die uebrigen Schluessel der Panel-Vorgabe (background-size, border, ...)
//   5. top und left ganz zum Schluss (Zeilen 66-70)
// Das laesst sich nicht durch simples Zusammenschuetten erreichen: rasterStyle
// liefert width/height/position/top/left als Block und schoebe damit position
// hinter die Vorgabe-Schluessel. Der Stil-String wird aber zeichengenau
// verglichen. Geprueft gegen alle drei Faelle des Hauptbildschirms
// (Info-Icons, Zahlungstasten, linke Leiste).
function knopfStil(eigen, vorgabe, eigenerStil, spalten, zeilen, index) {
  const raster = rasterStyle(spalten, zeilen, index, eigen);
  const aus = {};
  for (const s in (eigenerStil || {})) aus[s] = eigen[s];
  aus.width = raster.width;
  aus.height = raster.height;
  aus.position = 'absolute';
  for (const s in (vorgabe || {})) if (!(s in aus)) aus[s] = vorgabe[s];
  aus.top = raster.top;
  aus.left = raster.left;
  return aus;
}

export function PushPanel({ bridge, pfad, zeilen = 1, spalten = 1, knoepfe, style, knopfStyle, addclass }) {
  // CPushPanel.prepare legt fuer JEDE Rasterzelle einen Knopf an - fuer
  // unbesetzte einen unsichtbaren OHNE gerechnete Breite/Hoehe. Ohne die
  // fehlen im DOM Knoepfe (bei der Geparkte-Bons-Liste sofort aufgefallen:
  // 2 Knoepfe in einem 2x2-Raster).
  const zellen = knoepfe.slice();
  while (zellen.length < zeilen * spalten) zellen.push(null);

  return (
    <div {...bridge.el(pfad, 'CPushPanel', { properties: { styles: { ...style } } })}
         className={'CPushPanel' + (addclass ? ' ' + addclass : '')} style={style}>
      {zellen.map((k, i) => {
        if (!k) {
          const raster = rasterStyle(spalten, zeilen, i, knopfStyle || {});
          return (
            <div key={i} {...bridge.widget(`${pfad}:${i}`, 'CPushButton', { proto: CPushButton.prototype })}
                 className="CPushButton"
                 style={{ position: 'absolute', ...knopfStyle, visibility: 'hidden',
                          top: raster.top, left: raster.left }}>
              <p {...bridge.el(`${pfad}:${i}:0`, 'CCaption')} className="CCaption undefined" />
            </div>
          );
        }
        const attributes = {};
        if (k.param0 !== undefined) attributes.onMousedown_parameter_0 = k.param0;
        if (k.param1 !== undefined) attributes.onMousedown_parameter_1 = k.param1;
        if (k.param2 !== undefined) attributes.onMousedown_parameter_2 = k.param2;
        // combine()-Semantik, zeichengenau (core/tools-fnc.js:12-42): die
        // REIHENFOLGE kommt vom ersten Argument (den eigenen Stilen des
        // Knopfs), die WERTE bei Ueberschneidung vom zweiten (der Vorgabe des
        // Panels); nur Schluessel, die es noch nicht gibt, werden angehaengt.
        // Ein simples Zusammenschuetten macht beides falsch herum und
        // erzeugt "width; background-repeat; ..." statt
        // "background-repeat; ...; width" - der Stil-String wird aber
        // zeichengenau verglichen.
        const eigen = {};
        for (const s in (k.style || {}))
          eigen[s] = (knopfStyle && knopfStyle[s] !== undefined) ? knopfStyle[s] : k.style[s];
        for (const s in (knopfStyle || {}))
          if (!(s in eigen)) eigen[s] = knopfStyle[s];
        // properties.caption + reDraw (24.8.2026): Legacy-Code BENENNT Knoepfe
        // zur Laufzeit um. CSessionHandler.enableAllPOSSessions macht beim
        // Eroeffnen der Kassa aus "Kassa eroeffnen" ein "Kassa abschliessen":
        //   leftPad.children[11].properties.caption.value = ...
        //   leftPad.children[11].reDraw();
        // Ohne properties.caption wirft schon die Zuweisung ("Cannot set
        // properties of undefined"), der Zweig bricht ab und die Sitzung
        // landet auf dem Login-Bildschirm - in der Suite riss das fuenfzehn
        // Tests mit, weil die Kassa danach zu blieb.
        // Der Text wird deshalb AUS DEM HANDLE gelesen: was Legacy-Code
        // hineinschreibt, erscheint beim naechsten Rendern - dasselbe Muster
        // wie bei Pre und den Statuszeilen-Captions.
        // Der HANDLER kommt aus dem Handle, wenn fremder Code ihn dort
        // ausgetauscht hat (24.8.2026, teuer gefunden):
        // CSessionHandler.disableAllPOSSessions/enableAllPOSSessions tauschen
        // beim Schliessen bzw. Oeffnen der Kassa BEIDES am selben Knopf -
        // Beschriftung UND Handler:
        //   children[11].properties.caption.value = START_BALANCE;
        //   children[11].properties.events.onMousedown = sess.startPOS;
        // _handle setzt properties.events aber bei JEDEM Rendern auf das, was
        // die JSX erklaert. Der Tausch ueberlebte damit das naechste Rendern
        // nicht: der Knopf hiess "Kassa eroeffnen" und rief weiterhin endPOS -
        // ein Klick darauf startete also den ABSCHLUSS und zeigte dessen
        // Bankomat-Rueckfrage, statt die Eroeffnungsmaske zu oeffnen.
        // Deshalb: was fremder Code gesetzt hat, gewinnt.
        const vorhandener = bridge.handleAt(`${pfad}:${i}`);
        const handler = (vorhandener && vorhandener.properties && vorhandener.properties.events
                         && typeof vorhandener.properties.events.onMousedown === 'function')
          ? vorhandener.properties.events.onMousedown : k.handler;
        const knopfProps = bridge.widget(`${pfad}:${i}`, 'CPushButton', {
          events: { onMousedown: handler, onMouseout: true, onMouseup: true },
          attributes,
          proto: CPushButton.prototype,
          properties: { caption: { value: k.text } },
        });
        const knopfHandle = bridge.handleAt(`${pfad}:${i}`);
        // hasOwnProperty, NICHT "!handle.reDraw": reDraw wird vom Prototyp
        // GEERBT (CComponent), die einfache Pruefung ist also immer falsch
        // und der Ersatz griffe nie. Dann liefe das Legacy-reDraw, das ueber
        // builder.addElement neu baut - den vollen Builder haben Handles aber
        // nicht (die Bridge gibt ihnen nur einen Ersatz mit id/mainElement).
        // Ergebnis war "this.builder.addElement is not a function", und der
        // Kassenabschluss brach danach ab.
        // bridgeStandard: der allgemeine Ersatz der Bridge (bridge.js _handle)
        // - der hier ist genauer und gewinnt.
        if (knopfHandle && (!Object.prototype.hasOwnProperty.call(knopfHandle, 'reDraw')
                            || knopfHandle.reDraw.bridgeStandard)) {
          knopfHandle.reDraw = function () {
            bridge.update({ knopfStand: (bridge.state.knopfStand || 0) + 1 });
            return jQuery.Deferred().resolve().promise();
          };
        }
        const beschriftung = (knopfHandle && knopfHandle.properties && knopfHandle.properties.caption)
          ? knopfHandle.properties.caption.value : k.text;

        return (
          <div key={i} {...knopfProps}
               className={'CPushButton' + (k.klasse ? ' ' + k.klasse : '')}
               style={knopfStil(eigen, knopfStyle, k.style, spalten, zeilen, i)}>
            <p {...bridge.el(`${pfad}:${i}:0`, 'CCaption')} className={'CCaption ' + beschriftung}
               dangerouslySetInnerHTML={{ __html: beschriftung }} />
          </div>
        );
      })}
    </div>
  );
}

// --- Legacy-Knopf -> Deskriptor --------------------------------------------
// Die Screens der ersten Welle bauten ihre knoepfe-Felder von Hand aus SQL
// (CProductCategoriesMode). CMainPOS kann das nicht: seine sieben Panels
// werden von LEGACY-Methoden gefuellt (makeLeftPadButtons, makePaymentButtons,
// makeBonFncButtons, makeBonEditButtons, makeProdCatButtons, makeProdButtons,
// CInfoIcons.makeInfoIcons), die fertige CPushButton-INSTANZEN liefern. Diese
// Methoden bleiben unveraendert - sie sind Verhalten, nicht Darstellung.
// Hier wird nur uebersetzt.
//
// Zwei Legacy-Eigenheiten sind dabei zu beachten:
//  - properties.styles ist bei manchen Komponenten ein STRING (ein
//    Whitespace-<styles> in der XML wird von CXmlHandler.getObject zu
//    textContent). Ein String taugt nicht als React-style, deshalb nur
//    Objekte durchreichen - fuer alles andere galt im Legacy ohnehin nichts,
//    siehe den Waechter in components/componentbuilder.js.
//  - properties.caption.value fehlt bei unbeschrifteten Knoepfen (die
//    Info-Icons arbeiten nur ueber backgroundImage). Der Builder schreibt
//    dann "undefined" in die Klasse - PushPanel bildet das fuer LEERE Zellen
//    schon nach, hier bleibt der Text schlicht leer.
// Der Builder loest %ABSCHNITT.SCHLUESSEL% selbst auf, bevor er den Wert ins
// innerHTML schreibt (componentbuilder.js:88-94). Die make*Buttons-Methoden
// liefern deshalb ROHE Platzhalter, und wer sie unaufgeloest durchreicht,
// bekommt "%BUTTONS.OTHER_FUNCTIONS%" auf die Taste geschrieben - im Text UND
// in der Klasse. Hier zeichengenau dieselbe Pruefung und Zerlegung.
function textAufloesen(wert) {
  if (wert === undefined || wert === null) return '';
  const value = String(wert);
  if (value.search(/\%.*\.{1}.*\%/) != -1) {
    const teile = value.replace(/\%/g, '').split('.');
    return POS.lang.getText(teile[0], teile[1]);
  }
  return value;
}

export function knopfAusLegacy(btn) {
  if (!btn) return null;
  const p = btn.properties || {};
  const attr = p.attributes || {};
  const stil = (typeof p.styles === 'object' && p.styles !== null) ? p.styles : undefined;
  return {
    // ABSICHTLICH undefined statt '', wenn es gar keine Beschriftung gibt.
    // Der Builder laesst dann seinen hochgezogenen "var value" unzugewiesen
    // und schreibt " " + undefined in die Klasse - im DOM steht woertlich
    // class="CCaption undefined" (componentbuilder.js:84-108). PushPanel
    // bildet das nach: className 'CCaption ' + k.text ergibt genau das, und
    // dangerouslySetInnerHTML mit undefined laesst den Text leer.
    text: (p.caption && p.caption.value !== undefined) ? textAufloesen(p.caption.value) : undefined,
    handler: p.events && p.events.onMousedown,
    // Die Zusatzklasse steht an ZWEI Stellen: properties.class (aus der XML)
    // und btn.addclass (zweites Konstruktor-Argument, pushbutton.js:11) -
    // makeLeftPadButtons nutzt letzteres: new CPushButton(id, "LeftPadBtn").
    // Nur properties.class zu lesen kostete genau diese Klasse, und mit ihr
    // das halbe Layout der linken Leiste.
    klasse: p.class || btn.addclass,
    style: stil,
    param0: attr.onMousedown_parameter_0,
    param1: attr.onMousedown_parameter_1,
    param2: attr.onMousedown_parameter_2,
    // Rasterplatz fuer die xy-Anordnung (xySeitenAufteilen). Im Legacy
    // heissen die Felder anders: makeProdButtons schreibt die Spalten aus der
    // Tabelle BTN nach properties.page/xPos/yPos (mainpos.js). Ohne diese
    // Abbildung landen ALLE Produkttasten auf Rasterplatz 0 und der Rest der
    // Seite bleibt leer.
    seite: p.page,
    spalte: p.xPos,
    zeile: p.yPos,
  };
}

// --- Dynamisch erzeugte Legacy-Knoepfe ------------------------------------
// Fuer Knoepfe, die das Legacy frueher per addChild()+draw() nachtraeglich
// anhaengte (Scheintasten, OK-Tasten). Das DOM baut jetzt React, die WERTE
// bleiben im Handle, das die make*Buttons-Methode unveraendert befuellt.
//
// Zwei Fallen, beide teuer bezahlt:
//  1. HANDLER DURCHREICHEN, nicht "true" erklaeren. _handle ersetzt
//     properties.events bei JEDEM Rendern durch das von der JSX Erklaerte -
//     mit "true" ist die Funktion aus make*Buttons ueberschrieben, der Knopf
//     sieht richtig aus und tut NICHTS. Die Pixel-Galerie bleibt dabei gruen
//     (25.8.2026 am Wertkarten-Screen passiert).
//  2. Ohne Beschriftung schreibt der Builder woertlich "CCaption undefined"
//     in die Klasse - 'CCaption ' + undefined ergibt genau das.
export function KnopfAusHandle({ bridge, pfad }) {
  const h = bridge.handleAt(pfad);
  const pr = (h && h.properties) || {};
  const at = pr.attributes || {};
  const cap = pr.caption;
  const text = (cap && cap.value !== undefined) ? textAufloesen(cap.value) : undefined;
  return (
    <div {...bridge.widget(pfad, 'CPushButton', {
           events: {
             onMousedown: (pr.events && typeof pr.events.onMousedown === 'function')
               ? pr.events.onMousedown : true,
             onMouseout: true, onMouseup: true,
           },
           // NUR gesetzte Parameter durchreichen: widget() schreibt jeden
           // Schluessel per setAttribute, und ein undefined landet woertlich
           // als onmousedown_parameter_1="undefined" im DOM. Genau daran ist
           // der erste Versuch an okButtons[2] gescheitert (der hat nur
           // Parameter 0) - die Baseline hat das Attribut dort gar nicht.
           attributes: Object.fromEntries(
             ['onMousedown_parameter_0', 'onMousedown_parameter_1', 'onMousedown_parameter_2']
               .filter((k) => at[k] !== undefined).map((k) => [k, at[k]])),
           proto: CPushButton.prototype,
         })}
         className={'CPushButton' + (pr.class ? ' ' + pr.class : '')}
         // LEBENDES Stil-Objekt: die Legacy-Methoden schreiben weiter hinein
         // und rufen reStyle() - React rendert denselben Stand.
         style={pr.styles}>
      <p {...bridge.el(pfad + ':0', 'CCaption')} className={'CCaption ' + text}
         dangerouslySetInnerHTML={{ __html: text === undefined ? '' : text }} />
    </div>
  );
}

export function knoepfeAusLegacy(liste) {
  return (liste || []).map(knopfAusLegacy);
}

// --- Klickbare Ziffern -----------------------------------------------------
// CClickNumber: eine Ziffer mit unsichtbarer Klickflaeche darueber (obere
// Haelfte hoch, untere runter). Alle vier Klassen dieser Familie heissen im
// DOM "CClickNumber" - CMultiClickNumber/Date/Time setzen ihr this.name
// ebenfalls darauf (steht so in jeder Baseline).
const ZIFFER_TEXT = (links, oben) => ({
  position: 'absolute', top: oben, left: links,
  width: '4.0vw', height: '6vw', backgroundColor: '#FFFFFF',
});
const ZIFFER_TABELLE = (links, oben) => ({
  position: 'absolute', top: oben, left: links, width: '4.0vw', height: '6vw',
});
// xbackgroundColor steht so im Legacy (kein gueltiges CSS, der Browser
// verwirft es) - deshalb hier nur die wirksamen Angaben.
const ZIFFER_ZELLE = {
  borderStyle: 'solid', borderWidth: '1px', borderColor: '#555555',
  opacity: '0.5', height: '50%',
};
// Die Beschriftungen tragen ihre Schrift INLINE - eine Ziffer 6vh (aus
// CClickNumber), ein Trennzeichen 4.5vh (aus CMultiClick*). Das ist keine
// Kleinigkeit: ohne diese Angabe rendern die Ziffern in Browser-Standard
// (19px statt ~48px). Genau das ist beim ersten Wurf passiert und WEDER der
// Galerie NOCH der Suite aufgefallen, weil der Galerie-Eintrag der Bonsuche
// die Ziffern maskiert (sie stehen auf JETZT und wuerden taeglich driften).
// Aufgefallen ist es erst beim Vergleich mit einer LIVE gezogenen Vorlage.
const ZIFFER_SCHRIFT = { fontSize: '6vh', fontFamily: 'Verdana, Sans-Serif' };
const TRENNER_SCHRIFT = { fontSize: '4.5vh', fontFamily: 'Verdana, Sans-Serif' };

export function ClickNumber({ bridge, pfad, links, oben, doppelt }) {
  // `doppelt`: die erste Ziffer einer Datums-/Zeit-Gruppe hat im Legacy
  // ZWEI Textfelder und ZWEI Klicktabellen. Grund ist kein Zufall, sondern
  // CMultiClickDate.addDateComponent, das prepare() der ersten Ziffer ein
  // zweites Mal aufruft - die zweite Garnitur liegt deckungsgleich auf der
  // ersten und ist die, die tatsaechlich benutzt wird. Das wird hier
  // nachgebaut, nicht bereinigt: es steht so in der Baseline.
  const garnitur = (n) => (
    <Fragment key={n}>
      <div {...bridge.el(`${pfad}:${n}`, 'CTextLabel')} className="CTextLabel"
           style={ZIFFER_TEXT(links, oben)}>
        <ContainerTable bridge={bridge} pfad={`${pfad}:${n}:0`} zeilen={[[{
          inhalt: (sp) => <Caption bridge={bridge} pfad={`${sp}:0`} text="0" style={ZIFFER_SCHRIFT} />,
        }]]} />
      </div>
      <table {...bridge.el(`${pfad}:${n + 1}`, 'CTable')} className="CTable"
             style={ZIFFER_TABELLE(links, oben)}>
        <tbody {...bridge.el(`${pfad}:${n + 1}:0`, 'CTableBody')} className="CTableBody">
          {[CClickNumberLegacy.prototype.increase, CClickNumberLegacy.prototype.decrease].map((fnc, r) => (
            <tr key={r} {...bridge.el(`${pfad}:${n + 1}:0:${r}`, 'CRow')} className="CRow">
              <td {...bridge.widget(`${pfad}:${n + 1}:0:${r}:0`, 'CColumn', {
                    events: { onMousedown: fnc, onMouseout: true, onMouseup: true },
                    attributes: { onMousedown_parameter_0: bridge.childId(pfad) },
                    properties: { styles: { ...ZIFFER_ZELLE } },
                  })}
                  className="CColumn" style={ZIFFER_ZELLE}
                  dangerouslySetInnerHTML={{ __html: '&nbsp;' }} />
            </tr>
          ))}
        </tbody>
      </table>
    </Fragment>
  );

  return (
    <span {...bridge.el(pfad, 'CClickNumber', { proto: CClickNumberLegacy.prototype })}
          className="CClickNumber">
      {garnitur(0)}
      {doppelt ? garnitur(2) : null}
    </span>
  );
}

// CMultiClickNumber: mehrere Ziffern nebeneinander, optional mit Komma.
// Die Abstaende sind die des Legacy-prepare: 4vw je Ziffer, 1.2vw fuers Komma.
export function MultiClickNumber({ bridge, pfad, links, oben, vorkomma = 3, nachkomma = 2,
                                   ersteDoppelt = false,
                                   trennerStyle = { width: '2vw', height: '6vw' },
                                   style }) {
  const teile = [];
  let x = Number(String(links).replace('vw', ''));
  let n = 0;

  for (let i = 0; i < vorkomma; i++) {
    teile.push(<ClickNumber key={n} bridge={bridge} pfad={`${pfad}:${n}`}
                            links={x + 'vw'} oben={oben} doppelt={ersteDoppelt && i === 0} />);
    x += 4; n++;
  }
  if (nachkomma > 0) {
    teile.push(<Trenner key={n} bridge={bridge} pfad={`${pfad}:${n}`} text=","
                        links={x + 'vw'} oben={oben} style={trennerStyle} />);
    x += 1.2; n++;
    for (let i = 0; i < nachkomma; i++) {
      teile.push(<ClickNumber key={n} bridge={bridge} pfad={`${pfad}:${n}`}
                              links={x + 'vw'} oben={oben} />);
      x += 4; n++;
    }
  }

  // Reihenfolge der Schluessel ist nicht beliebig: der Legacy-Konstruktor
  // legt left/top an, die XML haengt ihre Angaben HINTEN dran.
  const stil = { left: links, top: oben, ...style };

  return (
    <span {...bridge.el(pfad, 'CClickNumber', {
            proto: CMultiClickNumberLegacy.prototype,
            properties: { styles: { ...stil } },
          })}
          className="CClickNumber" style={stil}>
      {teile}
    </span>
  );
}

// Das Trennzeichen zwischen zwei Ziffergruppen ("," / "." / ":").
function Trenner({ bridge, pfad, text, links, oben, style }) {
  const stil = { position: 'absolute', ...style, left: links, top: oben };
  return (
    <div {...bridge.el(pfad, 'CTextLabel')} className="CTextLabel" style={stil}>
      <ContainerTable bridge={bridge} pfad={`${pfad}:0`} zeilen={[[{
        inhalt: (sp) => <Caption bridge={bridge} pfad={`${sp}:0`} text={text} style={TRENNER_SCHRIFT} />,
      }]]} />
    </div>
  );
}

// CMultiClickDate: Tag.Monat.Jahr, je zwei Ziffern. Abstaende woertlich aus
// CMultiClickDate.prepare (7.2vw je Gruppe, 1.6vw je Trenner).
export function MultiClickDate({ bridge, pfad, links, oben }) {
  return (
    <MultiClickGruppe bridge={bridge} pfad={pfad} links={links} oben={oben}
                      proto={CMultiClickDateLegacy.prototype} trenner="."
                      gruppenBreite={7.2} trennerBreite={1.6}
                      trennerStyle={{ width: '2.5vw', height: '7.5vw' }} />
  );
}

// CMultiClickTime: Stunde:Minute. Andere Abstaende (10vw / 2.5vw).
export function MultiClickTime({ bridge, pfad, links, oben }) {
  return (
    <MultiClickGruppe bridge={bridge} pfad={pfad} links={links} oben={oben}
                      proto={CMultiClickTimeLegacy.prototype} trenner=":"
                      gruppenBreite={10} trennerBreite={2.5}
                      trennerStyle={{ width: '2.5vw', height: '7.5vw' }}
                      gruppen={2} />
  );
}

function MultiClickGruppe({ bridge, pfad, links, oben, proto, trenner, gruppenBreite,
                            trennerBreite, trennerStyle, gruppen = 3 }) {
  const teile = [];
  let x = Number(String(links).replace('vw', ''));
  let n = 0;

  for (let g = 0; g < gruppen; g++) {
    if (g > 0) {
      teile.push(<Trenner key={n} bridge={bridge} pfad={`${pfad}:${n}`} text={trenner}
                          links={x + 'vw'} oben={oben} style={trennerStyle} />);
      x += trennerBreite; n++;
    }
    teile.push(<MultiClickNumber key={n} bridge={bridge} pfad={`${pfad}:${n}`}
                                 links={x + 'vw'} oben={oben} vorkomma={2} nachkomma={0}
                                 ersteDoppelt />);
    x += gruppenBreite; n++;
  }

  return (
    <span {...bridge.el(pfad, 'CClickNumber', {
            proto, properties: { styles: { left: links, top: oben } },
          })}
          className="CClickNumber" style={{ left: links, top: oben }}>
      {teile}
    </span>
  );
}

// --- Verdrahtung der Ziffern nach dem Mount --------------------------------
// Die Legacy-Methoden arbeiten auf Feldern, die frueher prepare() gesetzt hat:
// eine Ziffer braucht text/caption/currentValue/numberArray, eine Gruppe ihre
// preCDArray, ein Datum seine day/month/year. Genau das wird hier gesetzt.
function verdrahteZiffer(bridge, pfad, doppelt, ziffern) {
  const zahl = bridge.handleAt(pfad);
  // Bei der doppelten Garnitur zeigt das Legacy auf die ZWEITE (sie liegt
  // oben und wird geklickt).
  const n = doppelt ? 2 : 0;
  const text = bridge.handleAt(`${pfad}:${n}`);
  text.caption = bridge.handleAt(`${pfad}:${n}:0:0:0:0:0`);
  zahl.text = text;
  zahl.table = bridge.handleAt(`${pfad}:${n + 1}`);
  zahl.topCell = bridge.handleAt(`${pfad}:${n + 1}:0:0:0`);
  zahl.bottomCell = bridge.handleAt(`${pfad}:${n + 1}:0:1:0`);
  zahl.currentValue = 0;
  zahl.numberArray = ziffern || [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  return zahl;
}

export function verdrahteMultiClickNumber(bridge, pfad, vorkomma = 3, nachkomma = 2,
                                          ersteDoppelt = false, ersteZiffern = null) {
  const gruppe = bridge.handleAt(pfad);
  gruppe.preCommaDigits = vorkomma;
  gruppe.postCommaDigits = nachkomma;
  gruppe.preCDArray = [];
  gruppe.postCDArray = [];

  let n = 0;
  for (let i = 0; i < vorkomma; i++, n++) {
    gruppe.preCDArray.push(verdrahteZiffer(bridge, `${pfad}:${n}`,
      ersteDoppelt && i === 0, i === 0 ? ersteZiffern : null));
  }
  if (nachkomma > 0) {
    n++; // Trenner
    for (let i = 0; i < nachkomma; i++, n++) {
      gruppe.postCDArray.push(verdrahteZiffer(bridge, `${pfad}:${n}`, false, null));
    }
  }
  return gruppe;
}

// Ziffernvorraete der ersten Stelle - woertlich aus addDateComponent/
// addTimeComponent (Tag 0-3, Monat 0-1, Stunde 0-2, Minute 0-5).
const ERSTE_ZIFFERN = {
  datum: [[0, 1, 2, 3], [0, 1], null],
  zeit: [[0, 1, 2], [0, 1, 2, 3, 4, 5]],
};

export function verdrahteMultiClickDate(bridge, pfad) {
  const datum = bridge.handleAt(pfad);
  const teile = [0, 2, 4].map((n, i) =>
    verdrahteMultiClickNumber(bridge, `${pfad}:${n}`, 2, 0, true, ERSTE_ZIFFERN.datum[i]));
  datum.day = teile[0];
  datum.month = teile[1];
  datum.year = teile[2];
  return datum;
}

export function verdrahteMultiClickTime(bridge, pfad) {
  const zeit = bridge.handleAt(pfad);
  zeit.hour = verdrahteMultiClickNumber(bridge, `${pfad}:0`, 2, 0, true, ERSTE_ZIFFERN.zeit[0]);
  zeit.minute = verdrahteMultiClickNumber(bridge, `${pfad}:2`, 2, 0, true, ERSTE_ZIFFERN.zeit[1]);
  return zeit;
}

// --- Datengitter -----------------------------------------------------------
// CDataGrid ist das einzige Widget, das seine Kinder ERST ZUR LAUFZEIT
// bekommt: die Screens rufen clear() und dann in einer Schleife
// addRowWithColumns(). Deshalb behaelt das Handle hier seine gewohnte
// imperative Schnittstelle - die aufrufenden Methoden bleiben so woertlich
// die alten -, nur schreibt sie jetzt in ein Datenmodell, und React rendert
// daraus. Aufbau wie im Legacy:
//   span.CDataGrid > table.CTable > tbody.CTableBody > tr.CRow > td.CColumn
//
// WARNUNG fuer spaetere Aenderungen: die ZEILEN eines Gitters stehen in
// KEINER Pixel-Baseline. Die Galerie-Eintraege maskieren sie weg, weil ihr
// Inhalt ueber Testlaeufe waechst (geparkte Bons, Zeiterfassung). Hier
// traegt das Bild also nicht - was hier kaputtgeht, faellt nur in
// tests/cases.js auf.

// Vor dem ersten Rendern aufzurufen (aus spec.prepare): legt das Handle an
// und haengt die Legacy-Schnittstelle daran. Das Handle wird beim Rendern
// WIEDERVERWENDET (gleicher Name, gleicher Prototyp), die Zeilen ueberleben
// also jedes Neuzeichnen.
export function gitterVorbereiten(bridge, pfad, eigenschaften = {}) {
  const gitter = bridge.handleVorbereiten(pfad, 'CDataGrid', {
    proto: CDataGridLegacy.prototype,
    properties: { canSelect: false, row: {}, column: {}, table: {}, ...eigenschaften },
  });

  gitter.rows = [];
  gitter.rowCount = 0;
  gitter.selectedRowIndex = -1;
  gitter.gitterPfad = pfad;

  const neuZeichnen = () => bridge.update({ gitterStand: (bridge.state.gitterStand || 0) + 1 });

  // Im Legacy baut prepare() Tabelle und Rumpf. Das tut jetzt React - der
  // Aufruf muss aber weiter gehen, die Screens warten auf sein Ergebnis.
  gitter.prepare = function () {
    this.prepared = true;
    return jQuery.Deferred().resolve().promise();
  };

  gitter.clear = function () {
    this.rowCount = 0;
    this.rows = [];
    this.selectedRowIndex = -1;
    neuZeichnen();
  };

  gitter.addRow = function (selectable) {
    const i = this.rows.length;
    // Der Platzhalter traegt dieselbe ID wie die spaeter gerenderte Zeile
    // und ein STABILES properties-Objekt: Screens setzen daran Styles, bevor
    // die Zeile im DOM steht (z.B. die Markierung der Vorauswahl). Beim
    // Rendern uebernimmt das Handle genau dieses Objekt.
    // Die Zeile erbt CComponent (24.8.2026): fremder Code behandelt sie wie
    // eine echte Komponente. Der Bonrenderer merkt sich die Zeilen in
    // gridLinksRow und ruft beim Neuzeichnen
    //   gridLinksRow[i].row.reStyle()      (bonrenderer.js)
    // - als reines Objektliteral hat sie diese Methode nicht, und das
    // Stornieren einer Position starb an "reStyle is not a function".
    // transmitted muss true sein, sonst kehrt reStyle sofort zurueck.
    const reihe = Object.create(CComponent.prototype);
    reihe.id = bridge.childId(`${pfad}:0:0:${i}`);
    // Die Zeilen-Voreinstellungen des Gitters (Hoehe, Trennlinie) kommen
    // im Legacy ueber combine dazu, bevor der Aufrufer etwas setzt.
    reihe.properties = combine({ styles: {}, events: {}, attributes: {} }, this.properties.row || {});
    reihe.children = [];
    reihe.transmitted = true;
    reihe.name = 'CRow';
    const zeile = {
      row: reihe,
      columns: [],
      selectable: selectable === undefined ? true : selectable,
      rowIndex: i,
    };
    this.rows.push(zeile);
    this.rowCount = i + 1;
    return zeile;
  };

  gitter.addRowWithColumns = function (columns, draw, selectable) {
    const zeile = this.addRow(selectable === undefined ? true : selectable);
    for (const spalte of columns) {
      // Reihenfolge WIE IM LEGACY: eine frische Spalte, darauf erst die
      // Spalten-Voreinstellungen des Gitters, dann die uebergebenen Werte.
      // Daraus ergibt sich auch die Schluesselreihenfolge im style-Attribut
      // (erst textAlign/verticalAlign/border aus der XML, dann width und
      // backgroundColor aus dem Aufrufer) - so steht es in der Live-Vorlage.
      const frisch = { styles: {}, events: {}, attributes: {} };
      const eigenschaften = combine(combine(frisch, this.properties.column || {}), spalte.properties);
      zeile.columns.push({ properties: eigenschaften });
    }
    // row.children ist im Legacy dieselbe Liste - CPosReceiptList liest
    // darueber den Wert der ersten Spalte (die Bonnummer).
    zeile.row.children = zeile.columns;
    if (draw) neuZeichnen();
    return zeile;
  };

  // Eine Zeile, die eine ganze KOMPONENTE aufnimmt. Der Bonrenderer legt so
  // je Bonzeile ein CPre an (CBonEntryOps.addNewPos) und liest es spaeter
  // ueber `rows[i].row.children[0].children[0]` zurueck. Das geht, weil das
  // Rendern der Bridge SYNCHRON ist (flushSync): nach neuZeichnen() steht
  // die Zelle im DOM, und das Legacy-Widget kann sich hineinzeichnen -
  // dieselbe Reihenfolge wie im Legacy (addRow -> addChild -> drawRow).
  gitter.addRowWithComponent = function (component, draw, selectable) {
    const zeile = this.addRow(selectable === undefined ? true : selectable);
    const frisch = { styles: {}, events: {}, attributes: {} };
    const spalte = { properties: combine(frisch, this.properties.column || {}) };
    zeile.columns.push(spalte);
    zeile.row.children = zeile.columns;

    neuZeichnen();

    // FEHLER 25.8.2026, live gefunden: das Rendern LEERT die Zellen aller
    // schon vorhandenen Zeilen. Der <td>-Knoten bleibt derselbe (nachgemessen),
    // aber sein Inhalt verschwindet - das <pre> darin hat das Legacy imperativ
    // gezeichnet, React kennt es nicht und raeumt es beim Abgleich weg.
    // Sichtbare Folge: auf dem Bon stand nur noch die ZULETZT gewaehlte
    // Position, alle vorherigen Zeilen waren leer (die Summe stimmte, weil die
    // Daten nie betroffen waren). Weder Galerie noch Suite haben das gesehen,
    // weil KEINE Baseline und kein Testfall je mehr als eine Position auf dem
    // Bon hatte.
    // Darum: nach jedem Rendern die Inhalte der bisherigen Zeilen neu zeichnen.
    // Die neue Zeile ist ausgenommen - sie wird gleich darunter gezeichnet.
    for (const andere of this.rows) {
      if (andere === zeile) continue;
      const k = andere.columns && andere.columns[0] && andere.columns[0].children
        && andere.columns[0].children[0];
      if (k && typeof k.draw === 'function') {
        k.transmitted = false;   // sonst haelt draw() sich fuer erledigt
        k.draw();
      }
    }

    const zelle = bridge.handleAt(`${pfad}:0:0:${zeile.rowIndex}:0`);
    zelle.addCreatedChild(component);
    // Damit `row.children[0].children[0]` die Komponente findet, teilt sich
    // das Modell die Kinderliste mit dem Handle.
    spalte.children = zelle.children;
    component.draw();
    return zeile;
  };

  // Legacy-reDraw wuerde das Element ueber den Builder NEU bauen und das
  // alte entfernen - bei einem von React verwalteten Knoten waere React
  // danach nicht mehr synchron. Hier heisst reDraw deshalb schlicht:
  // neu rendern.
  gitter.reDraw = function () {
    neuZeichnen();
    return jQuery.Deferred().resolve().promise();
  };

  return gitter;
}

// Dasselbe fuer jeden anderen React-Knoten, auf dem Legacy-Code reDraw()
// ruft (die Rahmen der Bonliste zum Beispiel).
// schluessel (24.8.2026): welcher Zustandswert hochgezaehlt wird. Vorgabe
// "gitterStand" wie bisher. CMainPOS braucht einen EIGENEN fuer die
// Produktauswahl: sie haengt in einem React.memo, das genau auf diesen Wert
// hoert. Zaehlte prodSelect.reDraw() den gemeinsamen gitterStand hoch, wuerde
// die Produktauswahl bei jeder Bon-Aenderung mitrendern (1691 Elemente, und
// das je angemeldetem Benutzer) - genau das, was das memo verhindern soll.
// Umgekehrt wuerde ein memo auf "prodStand", das niemand hochzaehlt, den
// Neuaufbau nach einem Datenimport verschlucken.
export function reDrawUeberReact(bridge, pfad, schluessel = 'gitterStand') {
  const handle = bridge.handleAt(pfad);
  if (!handle) return null;
  handle.reDraw = function () {
    bridge.update({ [schluessel]: (bridge.state[schluessel] || 0) + 1 });
    return jQuery.Deferred().resolve().promise();
  };
  return handle;
}

// --- Vorformatierter Text (CPre) -------------------------------------------
// Der Bonrenderer schreibt die Summenzeile in properties.value und ruft
// clear()/reValue()/reDraw(). Alle drei schreiben hier direkt ins DOM -
// genau das ist die sichtbare Wirkung, und React bleibt aussen vor.
export function preVorbereiten(bridge, pfad) {
  const handle = bridge.handleVorbereiten(pfad, 'CPre', {
    proto: CPreLegacy.prototype,
    properties: { value: '' },
  });
  const schreiben = () => {
    const el = document.getElementById(handle.id);
    if (el) el.innerHTML = handle.properties.value || '';
  };
  handle.reValue = schreiben;
  handle.reDraw = function () { schreiben(); return jQuery.Deferred().resolve().promise(); };
  handle.clear = function () { this.properties.value = ''; schreiben(); };
  return handle;
}

export function Pre({ bridge, pfad, style }) {
  const handle = bridge.handleAt(pfad);
  const wert = (handle && handle.properties && handle.properties.value) || '';
  return (
    <pre {...bridge.el(pfad, 'CPre', { proto: CPreLegacy.prototype })} className="CPre"
         style={style} dangerouslySetInnerHTML={{ __html: wert }} />
  );
}

// --- Caption, auf die Legacy-Code schreibt --------------------------------
// Fuer die drei Captions der Statuszeile (userInfo, versionInfo,
// dateTimeInfo). Die gewoehnliche <Caption> reicht nicht, weil hier zwei
// verschiedene Legacy-Wege auf dasselbe Element schreiben - und sie tun
// UNTERSCHIEDLICHES:
//   prepare()  baut die Caption neu: innerHTML UND Klasse. Der Builder
//              schreibt den Wert mit in die Klasse ("CCaption POSweb 2.0.0").
//   reValue()  setzt NUR innerHTML. Die Klasse behaelt den ALTEN Wert.
// CMainPOS.prepare ruft prepare(), CMainPOS.updateDateTime ruft alle 40s
// reValue() - die Klasse der Uhrzeit-Caption traegt danach dauerhaft den
// ERSTEN Zeitwert, der Text den aktuellen. Ein einzelner Wert kann das nicht
// abbilden, deshalb hier zwei: properties.value fuer den Text,
// klassenWert fuer die Klasse.
// Wie bei Pre/preVorbereiten schreiben die Legacy-Methoden direkt ins DOM
// und die JSX liest aus derselben Quelle - ein React-Neuzeichnen erzeugt
// damit genau das, was der Legacy-Aufruf hinterlassen hat.
export function captionVorbereiten(bridge, pfad, addclass = '') {
  const handle = bridge.handleVorbereiten(pfad, 'CCaption', {
    properties: { value: '' },
  });
  handle.addclass = addclass;
  if (handle.klassenWert === undefined) handle.klassenWert = '';
  const el = () => document.getElementById(handle.id);
  handle.reValue = function () {
    const n = el();
    if (n) n.innerHTML = this.properties.value || '';
  };
  // prepare() bringt die Klasse auf den aktuellen Wert - genau darin
  // unterscheidet es sich von reValue().
  handle.prepare = function () {
    this.klassenWert = this.properties.value || '';
    const n = el();
    if (n) {
      n.innerHTML = this.klassenWert;
      n.className = 'CCaption' + (this.addclass ? ' ' + this.addclass : '') + ' ' + this.klassenWert;
    }
  };
  return handle;
}

export function SchreibCaption({ bridge, pfad, style }) {
  const handle = bridge.handleAt(pfad);
  const text = (handle && handle.properties && handle.properties.value) || '';
  const klasse = (handle && handle.klassenWert) || '';
  const addclass = (handle && handle.addclass) || '';
  return (
    <p {...bridge.el(pfad, 'CCaption')} style={style}
       className={'CCaption' + (addclass ? ' ' + addclass : '') + ' ' + klasse}
       dangerouslySetInnerHTML={{ __html: text }} />
  );
}

export function DataGrid({ bridge, pfad, style, tableStyle }) {
  const gitter = bridge.handleAt(pfad);
  const zeilen = gitter ? gitter.rows : [];

  // Geloeschte Zeilen muessen auch aus dem Schattenbaum verschwinden, sonst
  // findet POS.getComponentById noch Handles zu DOM-Knoten, die es nicht
  // mehr gibt.
  const rumpf = bridge.handleAt(`${pfad}:0:0`);
  if (rumpf && rumpf.children.length > zeilen.length) {
    rumpf.children.length = zeilen.length;
    rumpf.childrenCount = zeilen.length;
  }

  return (
    <span {...bridge.el(pfad, 'CDataGrid', { proto: CDataGridLegacy.prototype })}
          className="CDataGrid" style={style}>
      <table {...bridge.el(`${pfad}:0`, 'CTable', { properties: { styles: { ...tableStyle } } })}
             className="CTable" style={tableStyle}>
        <tbody {...bridge.el(`${pfad}:0:0`, 'CTableBody')} className="CTableBody">
          {zeilen.map((zeile, r) => (
            <GridZeile key={r} bridge={bridge} pfad={`${pfad}:0:0:${r}`} gitter={gitter}
                       zeile={zeile} index={r} />
          ))}
        </tbody>
      </table>
    </span>
  );
}

function GridZeile({ bridge, pfad, gitter, zeile, index }) {
  const auswaehlbar = !!(gitter && gitter.properties && gitter.properties.canSelect);
  const spec = {
    properties: zeile.row.properties,
    proto: null,
  };
  if (auswaehlbar) {
    spec.events = { onMousedown: CDataGridLegacy.prototype.rowMouseDown, onMouseout: true, onMouseup: true };
    spec.attributes = { onMousedown_parameter_0: gitter.id, onMousedown_parameter_1: index };
  }
  const props = auswaehlbar ? bridge.widget(pfad, 'CRow', spec) : bridge.el(pfad, 'CRow', spec);

  // Die Auswahl setzt das Legacy per classList direkt ins DOM. Beim
  // Neu-Rendern muss sie aus dem Zustand kommen, sonst faellt sie weg.
  const markiert = gitter && gitter.selectedRowIndex === index;

  return (
    <tr {...props} className={'CRow' + (markiert ? ' selected' : '')}
        style={zeile.row.properties.styles}>
      {zeile.columns.map((spalte, c) => {
        // Zellen koennen EIGENE Ereignisse tragen - in der Bonliste haengt
        // an jeder Zelle die Bonnummer (onmousedown_parameter_1) und ein
        // "unlock". Nur dann wird ein Widget-Handle gebraucht.
        const eigeneEreignisse = Object.values(spalte.properties.events || {}).some((f) => f);
        const spec = { properties: spalte.properties };
        if (eigeneEreignisse) {
          spec.events = spalte.properties.events;
          spec.attributes = spalte.properties.attributes;
        }
        const props = eigeneEreignisse
          ? bridge.widget(`${pfad}:${c}`, 'CColumn', spec)
          : bridge.el(`${pfad}:${c}`, 'CColumn', spec);
        return (
          <td key={c} {...props} className="CColumn" style={spalte.properties.styles}
              dangerouslySetInnerHTML={{ __html: spalte.properties.value || '' }} />
        );
      })}
    </tr>
  );
}

// --- Reiter-Panel ----------------------------------------------------------
// CTabbedPushPanel: ein geblaettertes Panel mit den Reitern und je eines mit
// den Inhalten. Umgeschaltet wird ueber CTabbedPushPanel.tabSelect - das
// arbeitet direkt am DOM (Sichtbarkeit der Panels, Klasse "selected" am
// Reiter) und braucht dafuer die Felder itemsPanels/currentPanel/currentTab,
// die verdrahteTabbedPushPanel() nach dem Mount setzt.
// Daran haengt die Produktauswahl des Hauptbildschirms: 1 Reiter-Panel mit
// den Warengruppen, dahinter je ein Panel mit den Produkttasten der Gruppe.
export function TabbedPushPanel({ bridge, pfad, style, reiter, inhalte,
                                  reiterStyle, reiterZeilen = 1, reiterSpalten = 1,
                                  inhaltStyle, inhaltZeilen = 1, inhaltSpalten = 1,
                                  inhaltArt = 'xy' }) {
  // Der Reiter-Knopf bekommt Handler und Parameter NICHT von dem, der die
  // Knopfliste liefert, sondern vom Panel selbst - so macht es
  // CTabbedPushPanel.prepare (components/tabbedpushpanel.js:56-59):
  //   buttons[i].properties.events.onMousedown = this.tabSelect;
  //   buttons[i].properties.attributes.onMousedown_parameter_0 = this.id;
  //   buttons[i].properties.attributes.onMousedown_parameter_1 = i;
  // Ohne das fehlen den Warengruppen-Tasten beide Parameter-Attribute, und
  // das Umschalten zwischen den Reitern ist tot.
  const eigenId = bridge.childId(pfad);
  const reiterMitSchalter = (reiter || []).map((k, i) => (k ? {
    ...k,
    handler: CTabbedPushPanelLegacy.prototype.tabSelect,
    param0: eigenId,
    param1: i,
  } : k));

  return (
    <span {...bridge.el(pfad, 'CTabbedPushPanel', { proto: CTabbedPushPanelLegacy.prototype })}
          className="CTabbedPushPanel" style={style}>
      {/* Kind 0: die Reiter */}
      <PagedPushPanel bridge={bridge} pfad={`${pfad}:0`} knoepfe={reiterMitSchalter}
                      zeilen={reiterZeilen} spalten={reiterSpalten} style={reiterStyle} />
      {/* Kinder 1..n: je ein Panel mit den Inhalten eines Reiters */}
      {inhalte.map((knoepfe, i) => (
        <PagedPushPanel key={i} bridge={bridge} pfad={`${pfad}:${i + 1}`} knoepfe={knoepfe}
                        zeilen={inhaltZeilen} spalten={inhaltSpalten} art={inhaltArt}
                        style={i === 0 ? inhaltStyle
                          : { ...inhaltStyle, visibility: 'hidden' }} />
      ))}
    </span>
  );
}

export function verdrahteTabbedPushPanel(bridge, pfad, anzahlReiter) {
  const tab = bridge.handleAt(pfad);
  tab.tabsPanel = verdrahtePagedPushPanel(bridge, `${pfad}:0`);
  tab.itemsPanels = [];
  for (let i = 0; i < anzahlReiter; i++)
    tab.itemsPanels.push(verdrahtePagedPushPanel(bridge, `${pfad}:${i + 1}`));
  tab.currentPanel = tab.itemsPanels[0] || null;
  // Der aktive Reiter ist der erste Knopf der ersten Reiter-Seite - genau so
  // greift ihn CTabbedPushPanel.prepare ab (children[0].children[0]).
  tab.currentTab = bridge.handleAt(`${pfad}:0:0:0`);
  return tab;
}
